import { Injectable } from '@angular/core';
import { extractErrorMessage } from '../utils/utils';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { Contact } from '../models/contact';
import { ContactSummary } from "../models/contact-summary";
import { SupplierSummary } from '../models/supplier-summary';
import { SimilarityList } from '../models/similarity-list';

export interface ContactSimilarityCheckPayload {
  id: number | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

export interface ContactSimilarityRecord {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  nameSimilarity: number;
  emailSimilarity: number;
}

@Injectable({
  providedIn: 'root',
})
export class ContactService {
  baseUrl: string = environment.api_url;
  constructor(private readonly http: HttpClient) { }

  checkForSimilarContacts(data: ContactSimilarityCheckPayload): Observable<SimilarityList<ContactSimilarityRecord>> {
    let params = new HttpParams();
    for (const key in data) {
      const value = (data as any)[key];
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, value);
      }
    }
    return this.http.get<SimilarityList<ContactSimilarityRecord>>(`${this.baseUrl}/contacts/similar`, { params }).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  // GET all contacts
  getAllContacts(): Observable<Contact[]> {
    const params = new HttpParams().set('view', 'details');
    return this.http.get<Contact[]>(`${this.baseUrl}/contacts`, { params }).pipe(
      map(list => list.map(dto => new Contact(dto))),
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  // GET all contacts
  getSortedContacts(sortBy: string): Observable<Contact[]> {
    return this.http.get<Contact[]>(`${this.baseUrl}/contacts?sort=${sortBy}`).pipe(
      map(list => list.map(dto => new Contact(dto))),
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  getAllContactsSummary(): Observable<ContactSummary[]> {
    const params = new HttpParams().set('view', 'summary');
    return this.http.get<ContactSummary[]>(`${this.baseUrl}/contacts`, { params }).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  getAllContactSuppliers(): Observable<SupplierSummary[]> {
    return this.http.get<SupplierSummary[]>(`${this.baseUrl}/contacts/suppliers`,).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  // GET single contact by ID
  getContactById(id: number): Observable<Contact> {
    return this.http.get<Contact>(`${this.baseUrl}/contacts/${id}`).pipe(
      map(dto => {
        const sortedAffiliations = dto.affiliations?.slice().sort((a, b) =>
          a.recordedDate.localeCompare(b.recordedDate) || (a.organisation?.name ?? '').localeCompare(b.organisation?.name ?? ''));
        return new Contact({
          ...dto,
          affiliations: sortedAffiliations
        });
      }),
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  create(contact: Contact): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/contacts`, contact).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }
  update(contact: Contact): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/contacts/${contact.id}`, contact).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/contacts/${id}`).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

  setArchivedStatus(id: number, archived: boolean): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/contacts/${id}`, { archived: archived }).pipe(
      catchError(err => throwError(() => extractErrorMessage(err)))
    );
  }

}
